import React, { useState, useCallback, useContext, useMemo, useEffect } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { Stack } from '@fluentui/react';
import { MessageBar, MessageBarBody } from '@fluentui/react-message-bar';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { getLocationTargetTitleDetails, getAdScheduleDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { collapseExpander, Collapse, TextTitle, useCollapseState, ValidatedFixedPanel, ValidatedPanel } from '@bingads-webui-campaign/wizard-jumbo-card';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import { ValidationGroup } from '@bingads-webui-react/validation';
import {
  FormErrorGroup,
  entityIs,
  anyComp,
} from '@bingads-webui-campaign-react/form-error';
import { SEARCH_TYPE } from '@bingads-webui-react/bing-maps';
import { ValidatedLocationTargetSetting, useIsMounted } from '@bingads-webui-campaign-react/step-wizard-controls';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import {
  AgeRange,
  Gender,
  formatAgeRanges,
  formatGenders,
  adGroupNameProp,
  ReachEstimate,
  NetflixTCAgreement,
  ValidatedGenreTargets,
  formatGenreTargets,
  getGenreOptions,
  getDealGenreOptions,
  DealImpressionPanel,
} from '@bingads-webui-campaign-react/xandr-campaign';
import { withScope } from '@bingads-webui-react/scope-service';
import { useDefaultStyles, withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useFormService } from '@bingads-webui-react/form';
import {
  withControlErrorHandling,
  withUnvalidatedControlErrors,
} from '@bingads-webui-campaign-react/error-handling';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useWizardService, matchStepState, STEP_STATES } from '@bingads-webui-react/wizard';
import { ValidatedBoostPlacementTarget, placementTargetingDetails, contextualTargetDetails } from '@bingads-webui-campaign-react/boost-placement-target';
import { ValidatedContextualTarget } from '@bingads-webui-campaign-react/boost-contextual-target';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { Alert } from '@bingads-webui-react/alert';
import { isXandrCampaign, campaignShouldServeOnMSAN } from '@bingads-webui-campaign-react/constants';
import { ValidatedLegacyDateSetting, getDateSettingDetails } from '@bingads-webui-campaign/date-setting';
import { TargetingEstimation } from './components/targeting-estimation';
import { StepHeader } from '../../shared/step-header';
import { PredictiveTargetingHeader } from './components/predictive-targeting';
import { TargetingPreferences } from './components/targeting-preferences';
import { ValidatedBidAdjustment } from './components/audience-bid-adjustment';
import { SectionShimmer } from '../../shared/section-shimmer';
import { getAdGroupPanels } from '../../step-utils';
import { ERROR_ENTITY } from '../../common/error-entities';
import locationTargetIcon from '../../media/location-target.svg';
import genderTarget from '../../media/gender-target.svg';
import ageTarget from '../../media/age-target.svg';
import bidValueIcon from '../../media/bid-value.svg';
import placementTargetIcon from '../../media/placement-target.svg';
import contextualTargetIcon from '../../media/contextual-target.svg';
import adScheduleIcon from '../../media/ad-schedule.svg';
import startDateEndDateIcon from '../../media/start-date-end-date.svg';
import { getAdGroupStyles } from './adgroup-styles';
import {
  FLOWS,
  isAudienceAdGroupCreation,
  isAudienceAdGroupEdit as isAudienceAdGroupEditFlow,
  isAudience as isAudienceFlow,
  isEdit as isEditFlow,
  isCTVCampaign,
  isDealCampaign,
} from '../../step-constants';
import { getContextualTargetStyles } from '../../shared/styles/contextual-target-styles';
import { formatReachEstimateData, getTargetSettingsFromCriterions } from './utils';
import { XandrBidValue, AudienceBidValue } from '../../shared/components';
import { AdSchedule } from './components';

const adGroupLocationCriterions = anyComp(
  entityIs(ERROR_ENTITY.AdGroupLocation),
  entityIs(ERROR_ENTITY.AdGroupRadius),
  entityIs(ERROR_ENTITY.AdGroupNegativeLocation),
  entityIs(ERROR_ENTITY.AdGroupLocationIntent),
  entityIs(ERROR_ENTITY.AdGroupLocationTargetError)
);

const getPanelErrorProps = ({ needsAttention }) => (needsAttention ? {
  error: _TL_("* Something's wrong"),
} : {});

const ServerValidatedLocationTargetSetting = withControlErrorHandling(withUnvalidatedControlErrors(
  ValidatedLocationTargetSetting,
  { filter: adGroupLocationCriterions }
));

const ContextualTargetWithStyle = withDefaultStyles(ValidatedContextualTarget, getContextualTargetStyles);
export const AdGroupStepShellInternal = ({
  data,
  onDataChange: onDataChangeProp,
  scope,
  error,
}) => {
  const i18n = useContext(I18nContext);

  const isMounted = useIsMounted();

  const onDataChange = useCallback((...args) => {
    if (!isMounted.current) return;
    onDataChangeProp(...args);
  }, [isMounted, onDataChangeProp]);

  const serverContext = useContext(ServerContext);
  const classes = useDefaultStyles(getAdGroupStyles);

  const activity = useContext(ActivityContext);

  const { state } = useWizardService();

  const currentStates = state.toStrings()[1];

  const { flow, isDefaultAdGroupStepModel, adGroup: initialAdGroup } = state.context.wizardData;

  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();

  const {
    adGroup, bundleCountries, bundleLocationCriterions, campaign,
  } = data || {};

  const {
    CampaignSubType,
    CampaignSettings,
  } = campaign || {};

  const {
    locationTargets,
    ageRanges,
    genderTypes,
    BoostAdunitIds,
    BoostPublisherIds,
    BoostAccountIds,
    BoostTriggerIds,
    contextualTargets,
    genreTargets,
    deal,
    locationRestricted,
  } = adGroup || {};

  const [adScheduleView, setAdScheduleView] = useState(null);
  const setAdScheduleViewCallback = useCallback((view) => {
    setAdScheduleView(view);
  }, []);

  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);

  const { basicOptions } = useMemo(() => getAdGroupPanels(flow, serverContext), [flow, serverContext]);

  const [
    allAdGroupSettingsSections,
    adGroupSettingsExpandedSections,
    setAdGroupSettingsExpandedSections,
  ] = useCollapseState(
    basicOptions /* all sections */,
    ['ad-group-name', 'predictive-targeting'] /* initially expanded sections */
  );
  const {
    DynamicPermissions: {
      XandrNegativeTarget,
      AgeGenderDisclaimer,
      PTPerfEstimationUIUpdate,
      CampaignCreationPlannerIntegration,
      CampaignCreationPlannerIntegrationMultiAd,
      InMarketAudienceRedesign,
      CampaignLifetimeBudget,
      MaeLifetimeBudgetExclusion,
      AudienceUsePrivacyCheckV2,
    },
    CurrentCustomer: {
      Id: cid,
    },
    CurrentAccount: {
      Id: aid,
    },
    NetflixEnabledCountryCodes = 'GB',
  } = serverContext;

  const isCampaignLifetimeBudgetEnabled = CampaignLifetimeBudget && !MaeLifetimeBudgetExclusion;

  const useCampaignLevelDates = data?.campaign?.UseCampaignLevelDates;
  const startDate = useCampaignLevelDates ? data?.campaign?.StartDate : data?.adGroup?.StartDate;
  const endDate = useCampaignLevelDates ? data?.campaign?.EndDate : data?.adGroup?.EndDate;

  const allBasicExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAdGroupSettingsExpandedSections }),
    [setAdGroupSettingsExpandedSections]
  );

  const shouldServeOnMSAN = campaignShouldServeOnMSAN(CampaignSettings);
  const isXandrServing = isXandrCampaign(CampaignSubType) && !shouldServeOnMSAN;

  const isAudienceAdGroupCreate = useMemo(() => isAudienceAdGroupCreation(flow), [flow]);
  const isAudienceAdGroupEdit = useMemo(() => isAudienceAdGroupEditFlow(flow), [flow]);
  const isAudience = useMemo(() => isAudienceFlow(flow), [flow]);
  const isEdit = useMemo(() => isEditFlow(flow), [flow]);
  const isCTVFlow = useMemo(() => isCTVCampaign(flow), [flow]);
  const isDealFlow = useMemo(() => isDealCampaign(flow), [flow]);
  const isCTVOrDeal = isCTVFlow || isDealFlow;

  const isDealTypeRestricted = deal && _.isEqual('Restricted', deal.Type);
  const shouldShowDealLocationTarget = (deal && !locationRestricted) || !isDealTypeRestricted;
  const hideLocationTarget = isDealFlow ? !shouldShowDealLocationTarget : false;
  const hideGenreTarget = isDealFlow ? !(deal && deal.Genre) : false;
  const genreOptions = useMemo(() => _.sortBy(isDealFlow ? getDealGenreOptions(i18n) : getGenreOptions(i18n), 'name'), [i18n, isDealFlow]);
  const dealCountryCode = deal && deal.TwoLetterCountryCode;
  const dealSupportedLocationType = deal && deal.SupportedLocationType;

  const errors = useMemo(() => {
    if (error && _.isArray(error)) {
      return error;
    } else if (data && data.errors && data.errors.length > 0) {
      return data.errors;
    }
    return [];
  }, [data, error]);

  const showAllValidationErrorsMemo = useMemo(() => showAllValidationErrors || (data && data.errors && data.errors.length > 0), [showAllValidationErrors, data]);

  const BidValue = isXandrServing ? XandrBidValue : AudienceBidValue;

  const onValidate = (result) => {
    if (data && data.adGroup) {
      const updatedData = {
        data: {
          ...data,
          adGroup: {
            ...data.adGroup,
            ...result.data,
          },
        },
        isValid: result.isValid,
      };

      let targetSetting = null;

      if (isAudience) {
        targetSetting = getTargetSettingsFromCriterions(updatedData.data.adGroup, isAudienceAdGroupEdit);
        updatedData.data.adGroup.TargetSetting = targetSetting;
      }
      onDataChange(updatedData);
    }
  };

  const isValidTargets = useMemo(() => {
    if (data && data.adGroup) {
      if (PTPerfEstimationUIUpdate ||
        data.adGroup.UseOptimizedTargeting === false
      ) {
        return true;
      }
    }
    return false;
  }, [PTPerfEstimationUIUpdate, data]);

  const NetflixEnabledCountryCodesArr = useMemo(() => NetflixEnabledCountryCodes.split(','), [NetflixEnabledCountryCodes]);
  const BundleCountriesCodesArr = useMemo(() => (bundleLocationCriterions && bundleLocationCriterions.map(c => c && c.Criterion && c.Criterion.LocationCriterion && c.Criterion.LocationCriterion.CountryCode)) || [], [bundleLocationCriterions]);

  const expiredAdGroupError = _.filter(error, err => err.code === 'CampaignServiceCannotUpdateAdGroupInExpiredState');
  const bidAmountsLessThanFloorPriceAdGroupError = _.filter(error, err => err.code === 'CampaignServiceBidAmountsLessThanFloorPrice');

  const shouldShowNetflixTC = useMemo(() =>
    isCTVFlow &&
    locationTargets &&
    (
      locationTargets.currentLevelSettings.targetOption === 'everywhere' ||
      (
        locationTargets.currentLevelSettings.targetOption === 'bundle'
        && BundleCountriesCodesArr.some(bundleCountry => NetflixEnabledCountryCodesArr.indexOf(bundleCountry) >= 0)
      ) ||
      (
        locationTargets.currentLevelSettings.targetOption === 'specific' &&
        _.some(
          locationTargets.currentLevelSettings.currentLocations,
          location => location.SelectionStatus === 'Included'
            && location.Criterion.LocationCriterion
            && NetflixEnabledCountryCodesArr.indexOf(location.Criterion.LocationCriterion.CountryCode) >= 0
        )
      )
    ), [isCTVFlow, locationTargets, NetflixEnabledCountryCodesArr, BundleCountriesCodesArr]);

  useEffect(() => {
    if (activity && matchStepState(state, STEP_STATES.LOADING)) {
      activity.signal(`onAdGroupStepLoading - ${currentStates}`);
    }

    if (activity && matchStepState(state, STEP_STATES.SAVING)) {
      activity.signal(`onAdGroupStepSaving - ${currentStates}`);
    }
  }, [activity, currentStates, state]);

  return (
    <div className={classNames(classes.adgroupShell, 'adgroup-shell', 'ad-group-step')}>
      <ActuationGroup actuationProps={{ isActive: false }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors, showAllValidationErrors: showAllValidationErrorsMemo }}>
            <ValidationGroup
              validationProps={{
                schema: AdGroup,
                onValidate,
                data: (data && data.adGroup) || {},
                i18n: legacyI18n,
              }}
            >
              <Stack className={classes.adgroupOptions} horizontal tokens={{ childrenGap: 30 }}>
                <div className={classNames('adgroup-step')}>
                  {!isEdit &&
                    <StepHeader
                      title={_TL_('Ad group, audiences, & targets')}
                    />
                  }
                  <div className="base-options-stack">
                    <Collapse allPanelItems={allAdGroupSettingsSections} checkItems skipItemsTypeCheck activeKeys={adGroupSettingsExpandedSections} setActiveKeys={setAdGroupSettingsExpandedSections}>
                      <ValidatedFixedPanel
                        key="ad-group-name"
                        title={<TextTitle titleKey={_TL_('Ad group name')} topicKey="pop_BA_CampaignCreate_AudTargets_AdGroupName" />}
                        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      >
                        <RemainingCharsValidatedTextField
                          placeholder={_TL_('Enter a name for your ad group')}
                          id="input-ad-group-name"
                          validationProps={{
                            field: 'Name',
                            schema: AdGroup,
                          }}
                          i18n={i18n}
                          formErrorProps={{ filter: adGroupNameProp }}
                        />
                      </ValidatedFixedPanel>
                      {(isCTVOrDeal || isEdit) && campaign && campaign.BiddingScheme && campaign.BiddingScheme.Type !== 'MaxConversions' &&
                        <ValidatedFixedPanel
                          key="bid-value"
                          title={<TextTitle titleKey={_TL_('Default bid')} icon={bidValueIcon} topicKey="pop_BA_CampaignCreate_Budget_DefaultBid" />}
                          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                        >
                          {
                            data && data.adGroup && data.campaign ? (
                              <BidValue
                                data={data}
                                onDataChange={onDataChange}
                                legacyI18n={legacyI18n}
                                isEdit={isEdit}
                                isPremiumStreaming={isCTVFlow}
                              />
                            ) : (<SectionShimmer count={3} />)
                          }
                        </ValidatedFixedPanel>
                      }
                      <ValidatedPanel
                        key="start-end-date"
                        noOverflowHidden
                        title={<TextTitle
                          icon={startDateEndDateIcon}
                          titleKey={_TL_('Start and end dates')}
                          details={`${getDateSettingDetails(i18n, startDate, endDate, useCampaignLevelDates, isCampaignLifetimeBudgetEnabled)}`}
                          panelKey="start-end-date"
                        />}
                        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      >
                        <ValidatedLegacyDateSetting
                          i18n={i18n}
                          validationProps={{
                            stopPropagation: true,
                            data: {
                              StartDate: startDate,
                              EndDate: endDate,
                              TimeZone: data && data.campaign && data.campaign.TimeZone,
                              isCreate: initialAdGroup === null || (initialAdGroup && new Date(initialAdGroup.StartDate) > new Date()) || isDefaultAdGroupStepModel,
                            },
                          }}
                          allowStartDateSelection={!useCampaignLevelDates}
                          allowNoEndDateSelection={!useCampaignLevelDates}
                          allowEndDateSelection={!useCampaignLevelDates}
                        />
                        {useCampaignLevelDates && (
                          <div style={{ marginTop: '10px' }}>
                            <MessageBar intent="info" layout="auto">
                              <MessageBarBody>
                                {i18n.getString(_TL_('Start and end dates for this campaign are managed at the campaign level. To edit, go to your campaign settings.'))}
                              </MessageBarBody>
                            </MessageBar>
                          </div>
                        )}
                      </ValidatedPanel>
                      <ValidatedPanel
                        key="placement-target"
                        title={<TextTitle
                          icon={placementTargetIcon}
                          titleKey={_TL_('Placement targeting')}
                          details={placementTargetingDetails}
                        />}
                      >{
                          data && data.adGroup ? (
                            <ValidatedBoostPlacementTarget
                              i18n={i18n}
                              hideHeader
                              boostSetting={{
                                adUnitIds: BoostAdunitIds,
                                publisherIds: BoostPublisherIds,
                                accountIds: BoostAccountIds,
                                triggerIds: BoostTriggerIds,
                              }}
                            />
                          ) : (<SectionShimmer />)
                        }
                      </ValidatedPanel>
                      <ValidatedPanel
                        key="contextual-target"
                        title={<TextTitle
                          titleKey={_TL_('Contextual targeting')}
                          icon={contextualTargetIcon}
                          details={contextualTargetDetails}
                        />}
                      >{
                          data && data.adGroup ? (
                            <ContextualTargetWithStyle
                              i18n={i18n}
                              validationProps={{
                                data: {
                                  contextualTargets,
                                },
                              }}
                              hideHeader
                            />
                          ) : (<SectionShimmer />)
                        }
                      </ValidatedPanel>
                      {
                        !hideLocationTarget &&
                        <ValidatedPanel
                          key="location-target"
                          data={data}
                          onDataChange={onDataChange}
                          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                          actuationProps={{
                            expand: allBasicExpand,
                            expandKey: 'location-target',
                          }}
                          title={<TextTitle
                            titleKey={_TL_('Location')}
                            key="location-target"
                            topicKey="pop_BA_CampaignCreate_Settings_LocationTargeting"
                            panelKey="location-target"
                            details={getLocationTargetTitleDetails(i18n, locationTargets, true, '', true)}
                            icon={locationTargetIcon}
                          />}
                        >
                          {locationTargets ? (
                            <ServerValidatedLocationTargetSetting
                              validationProps={{
                                data: locationTargets,
                              }}
                              bundleCountries={bundleCountries}
                              bundleLocationCriterions={bundleLocationCriterions}
                              isRadiusExcludeEnabled={!isAudience || !isXandrServing}
                              allowNegativeLocationTargeting={isAudience ? true : XandrNegativeTarget}
                              showSearchTypeOptions={!isXandrServing}
                              defaultSelectedSearchType={SEARCH_TYPE.Boundary}
                              isBrandAwarenessCampaignScenario={isXandrServing}
                              hideIntent
                              scope={scope}
                              isAdGroupLevel={isCTVOrDeal ? false : (isEdit || isAudienceAdGroupCreate)}
                              showUserParentLevelToggle={isCTVOrDeal ? false : (isEdit || isAudienceAdGroupCreate)}
                              bidStrategyType="placeholder"
                              strings={{
                                description: i18n.getString(_TL_('For example, a country/region, state/province, or ZIP/postal code.')),
                              }}
                              allowBidAdjustment={isAudienceAdGroupEdit}
                              dealInfo={{ isForDeal: isDealFlow && shouldShowDealLocationTarget, dealCountryCode, dealSupportedLocationType }}
                              // For the current state, we use the GEO check for location setting validation.
                              // In the future, we will distinguish TARGETABLE deal and GEO deal based on translated VP.
                            />
                          ) : (<SectionShimmer />)}
                        </ValidatedPanel>
                      }
                      {
                        shouldShowNetflixTC &&
                        <ValidatedFixedPanel
                          key="netflix-tc"
                          title={<TextTitle
                            titleKey={_TL_('Netflix Terms and Conditions')}
                          />}
                        >
                          <NetflixTCAgreement
                            i18n={i18n}
                            cid={cid}
                            aid={aid}
                          />
                        </ValidatedFixedPanel>
                      }
                      <ValidatedPanel
                        key="gender-target"
                        title={<TextTitle
                          titleKey={_TL_('Gender')}
                          key="gender-target"
                          panelKey="gender-target"
                          details={formatGenders(genderTypes, i18n)}
                          topicKey="pop_BA_CampaignCreate_AudTargets_Gender"
                          icon={genderTarget}
                        />}
                        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                        actuationProps={{
                          expand: allBasicExpand,
                          expandKey: 'gender-target',
                        }}
                      >
                        {
                          genderTypes ? (
                            <>
                              <Gender
                                i18n={i18n}
                                genders={genderTypes}
                              />
                              {
                                isEdit && !isCTVOrDeal && (
                                  <ValidatedBidAdjustment
                                    data={data}
                                    validationProps={{
                                      data: data.adGroup,
                                      onValidate,
                                      stopPropagation: true,
                                    }}
                                    criterionType="Gender"
                                  />
                                )
                              }
                            </>
                          ) : (<SectionShimmer />)
                        }
                      </ValidatedPanel>
                      <ValidatedPanel
                        key="age-target"
                        title={<TextTitle
                          titleKey={_TL_('Age')}
                          key="age-target"
                          topicKey="pop_BA_CampaignCreate_AudTargets_Age"
                          panelKey="age-target"
                          details={formatAgeRanges(ageRanges, i18n)}
                          icon={ageTarget}
                        />}
                        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                        actuationProps={{
                          expand: allBasicExpand,
                          expandKey: 'age-target',
                        }}
                      >
                        {
                          ageRanges ? (
                            <>
                              <AgeRange
                                i18n={i18n}
                                ageRanges={ageRanges}
                              />
                              {
                                isEdit && !isCTVOrDeal && (
                                  <ValidatedBidAdjustment
                                    data={data}
                                    validationProps={{
                                      data: data.adGroup,
                                      onValidate,
                                      stopPropagation: true,
                                    }}
                                    criterionType="Age"
                                  />
                                )
                              }
                            </>
                          ) : (<SectionShimmer />)
                        }
                      </ValidatedPanel>
                      {
                        !hideGenreTarget && (
                          <ValidatedPanel
                            key="genre-targets"
                            title={<TextTitle
                              titleKey={_TL_('Genre')}
                              key="genre-targets"
                              topicKey="pop_BA_CampaignCreate_AudTargets_Genre"
                              panelKey="genre-targets"
                              details={formatGenreTargets({ genreTargets, genreOptions, i18n })}
                            />}
                            attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                            actuationProps={{
                              expand: allBasicExpand,
                              expandKey: 'genre-targets',
                            }}
                          >
                            {
                              genreTargets ? (
                                <>
                                  <ValidatedGenreTargets
                                    i18n={i18n}
                                    genreTargets={genreTargets}
                                    genreOptions={genreOptions}
                                  />
                                </>
                              ) : (<SectionShimmer />)
                            }
                          </ValidatedPanel>
                        )
                      }
                      <ValidatedFixedPanel
                        key="predictive-targeting"
                        title={data ? <PredictiveTargetingHeader data={data.adGroup} /> : <SectionShimmer count={1} />}
                        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      >
                        <div />
                      </ValidatedFixedPanel>
                      <ValidatedPanel
                        key="ad-schedule"
                        title={adGroup ? <TextTitle
                          titleKey={_TL_('Ad schedule')}
                          key="ad-schedule"
                          panelKey="ad-schedule"
                          details={deal ? getDateSettingDetails(i18n, adGroup.StartDate, adGroup.EndDate) : `${getDateSettingDetails(i18n, adGroup.StartDate, adGroup.EndDate)}: ${getAdScheduleDetails(i18n, adScheduleView && adScheduleView.current, true)}`}
                          icon={adScheduleIcon}
                        /> : <SectionShimmer count={1} />}
                      >
                        {
                          adGroup && campaign ? (
                            <AdSchedule
                              adGroup={adGroup}
                              campaign={campaign}
                              showBidAdjustmentColumn={!isCTVOrDeal}
                              hideUseCampaignSetting={isCTVOrDeal}
                              setAdScheduleViewCallback={setAdScheduleViewCallback}
                              isCreate={!isEdit}
                            />
                          ) : (<SectionShimmer />)
                        }
                      </ValidatedPanel>
                    </Collapse>
                  </div>
                  {!isDealFlow && <TargetingPreferences data={data} onDataChange={onDataChange} error={error} isDateSettingsDisplayed={!isAudienceFlow} />}
                  {expiredAdGroupError.length > 0 && (
                    <div style={{ marginBottom: '4px' }}>
                      <Alert
                        message={expiredAdGroupError[0].message}
                        level="Error"
                        icon={<div className="alert-image" />}
                        dismissible={false}
                      />
                    </div>
                  )}
                  {bidAmountsLessThanFloorPriceAdGroupError.length > 0 && (
                    <div style={{ marginBottom: '4px' }}>
                      <Alert
                        message={bidAmountsLessThanFloorPriceAdGroupError[0].message}
                        level="Error"
                        icon={<div className="alert-image" />}
                        dismissible={false}
                      />
                    </div>
                  )}
                  {AgeGenderDisclaimer && <Alert
                    message={i18n.getString(_TL_('If you’re advertising in connection with any financial, insurance, education, career and employment, and/or housing services, you should <b>not</b> use individuals’ demographics—such as age, gender, location, etc.—for the purpose of personalizing advertising, segmenting, or profiling customers.'))}
                    level="Info"
                    icon={(<div className="alert-image" />)}
                    dismissible={false}
                  />}
                </div>
                <div className={classNames(isEdit ? classes.estimationPartEditFlow : classes.estimationPart, 'estimation-part')}>
                  {isValidTargets && isAudience && data.adGroup && data.campaign &&
                    <TargetingEstimation
                      data={data}
                      isCampaignCreationPlannerIntegrationEnabled={CampaignCreationPlannerIntegration}
                      PTPerfEstimationUIUpdate={PTPerfEstimationUIUpdate}
                      isMultiAdFormatEnabled={CampaignCreationPlannerIntegrationMultiAd}
                      validationProps={{ field: 'reachEstimation' }}
                      showBreakdown={InMarketAudienceRedesign}
                      isAudienceAdGroupEdit={isAudienceAdGroupEdit}
                      audienceUsePrivacyCheck={AudienceUsePrivacyCheckV2}
                    />
                  }
                  {(flow === FLOWS.VIDEO_CAMPAIGNS || flow === FLOWS.DISPLAY_CAMPAIGNS || isCTVFlow)
                    && adGroup && campaign && <ReachEstimate
                      i18n={i18n}
                      permissions={serverContext.Permissions}
                      shouldServeOnMSAN
                      targets={formatReachEstimateData({
                        adGroup,
                        bundleCountries,
                        campaignSubType: campaign.campaignSubType,
                        campaignBiddingScheme: campaign.BiddingScheme,
                      })}
                      accountId={serverContext.CurrentAccount.Id}
                      customerId={serverContext.CurrentCustomer.Id}
                      validationProps={{ field: 'reachEstimation' }}
                    />
                  }
                  {
                    isDealFlow && deal && adGroup && campaign &&
                    <DealImpressionPanel
                      targets={formatReachEstimateData({
                        adGroup,
                        bundleCountries,
                        campaignSubType: campaign.campaignSubType,
                        campaignBiddingScheme: campaign.BiddingScheme,
                      })}
                      i18n={i18n}
                      serverContext={serverContext}
                      deal={deal}
                    />
                  }
                </div>
              </Stack>
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

AdGroupStepShellInternal.propTypes = {
  data: PropTypes.shape({
    adGroup: PropTypes.shape({
      UseOptimizedTargeting: PropTypes.bool,
      StartDate: PropTypes.string,
      EndDate: PropTypes.string,
      Id: PropTypes.number,
    }),
    campaign: PropTypes.shape({
      BiddingScheme: PropTypes.shape({
        Type: PropTypes.string,
      }),
      TimeZone: PropTypes.string,
      UseCampaignLevelDates: PropTypes.bool,
      EndDate: PropTypes.string,
      StartDate: PropTypes.string,
    }),
    exclusions: PropTypes.shape({
      adgroupExclusions: PropTypes.shape({}),
    }),
    errors: PropTypes.arrayOf(PropTypes.shape({
      message: PropTypes.string,
      property: PropTypes.string,
      code: PropTypes.string,
    })),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.arrayOf(PropTypes.shape({
    message: PropTypes.string,
    property: PropTypes.string,
    code: PropTypes.string,
  })).isRequired,
  scope: PropTypes.shape({}).isRequired,
};

export const AdGroupStepShell = withScope(AdGroupStepShellInternal);
